/* Devices — fleet list.
 *
 * Renders a table of devices with status pills. Each row links to the
 * device-detail page. Uses Card primitive for the surface. */

import { Link } from 'react-router-dom';
import { Card, Pill, type PillTone } from '@sp/design-system-react';
import { useDevices, type DeviceSummary } from '../hooks/useDevices';

function statusTone(status: DeviceSummary['status']): PillTone {
  switch (status) {
    case 'ok': return 'ok';
    case 'watch': return 'watch';
    case 'warn': return 'warn';
    case 'alarm': return 'alarm';
    default: return 'neutral';
  }
}

function formatLastSeen(iso: string | null): string {
  if (!iso) return 'never';
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return 'unknown';
  const diffMs = Date.now() - ts;
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  return `${diffD}d ago`;
}

export function DevicesPage(): JSX.Element {
  const devices = useDevices();

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-baseline justify-between">
        <h1 className="text-h1" style={{ color: 'var(--ink-primary)' }}>
          Devices
        </h1>
        <span className="text-caption" style={{ color: 'var(--ink-tertiary)' }}>
          {devices.data?.length ?? 0} total
        </span>
      </header>

      <Card variant="surface" density="dense">
        {devices.isLoading && (
          <div className="p-6 text-center text-body" style={{ color: 'var(--ink-tertiary)' }}>
            Loading…
          </div>
        )}
        {devices.isError && (
          <div className="p-6 text-center text-body" style={{ color: 'var(--signal-alarm)' }}>
            Failed to load devices.
          </div>
        )}
        {devices.data && devices.data.length === 0 && (
          <div className="p-6 text-center text-body" style={{ color: 'var(--ink-tertiary)' }}>
            No devices yet.
          </div>
        )}
        {devices.data && devices.data.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-hairline)' }}>
                <th className="px-3 py-3 text-left text-label" style={{ color: 'var(--ink-tertiary)' }}>
                  Name
                </th>
                <th className="px-3 py-3 text-left text-label" style={{ color: 'var(--ink-tertiary)' }}>
                  Type
                </th>
                <th className="px-3 py-3 text-left text-label" style={{ color: 'var(--ink-tertiary)' }}>
                  Location
                </th>
                <th className="px-3 py-3 text-left text-label" style={{ color: 'var(--ink-tertiary)' }}>
                  Status
                </th>
                <th className="px-3 py-3 text-left text-label" style={{ color: 'var(--ink-tertiary)' }}>
                  Last seen
                </th>
              </tr>
            </thead>
            <tbody>
              {devices.data.map((d) => (
                <tr
                  key={d.device_id}
                  style={{ borderBottom: '1px solid var(--border-hairline)' }}
                  className="transition-colors hover:bg-bg-sunken"
                >
                  <td className="px-3 py-3">
                    <Link
                      to={`/devices/${d.device_id}`}
                      className="font-medium hover:underline"
                      style={{ color: 'var(--ink-primary)' }}
                    >
                      {d.device_name}
                    </Link>
                  </td>
                  <td className="px-3 py-3" style={{ color: 'var(--ink-secondary)' }}>
                    {d.device_type}
                  </td>
                  <td className="px-3 py-3" style={{ color: 'var(--ink-secondary)' }}>
                    {d.location ?? '—'}
                  </td>
                  <td className="px-3 py-3">
                    <Pill tone={statusTone(d.status)} label={d.status} size="sm" />
                  </td>
                  <td className="px-3 py-3" style={{ color: 'var(--ink-tertiary)' }}>
                    {formatLastSeen(d.last_seen_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
